export const ATTRIBUTION_STORAGE_KEY = 'qlearn_acquisition_attribution';
export const ATTRIBUTION_MAX_AGE_DAYS = 90;

export const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ref',
  'platform',
  'qlink_id',
  // Q7-T1 live-session conversion keys (B3 cure). The backend conversion URL
  // (LiveSessionManager.conversionUrl) emits exactly these three; the allowlist is
  // drop-by-default, so adding them here captures the session-attribution round trip
  // (persisted on landing via App.jsx persistAttribution, then carried to the backend
  // by getBackendAttribution) while every unlisted/sensitive key stays dropped.
  'acqSource',
  'sessionId',
  'directorId',
];

const MAX_VALUE_LENGTH = 500;
const MAX_ATTRIBUTION_AGE_MS = ATTRIBUTION_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
const QLINK_VIEW_PATH_PATTERN = /^\/question\/view\/([^/?#]+)/;
const QLINK_OAUTH_STATE_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ref',
];

const normalizeValue = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.slice(0, MAX_VALUE_LENGTH);
};

const buildAttributionSearchParams = (attributionParams = {}) => {
  const params = new URLSearchParams();

  ATTRIBUTION_PARAM_KEYS.forEach((key) => {
    const value = normalizeValue(attributionParams?.[key]);
    if (value) {
      params.set(key, value);
    }
  });

  return params;
};

const buildPathFromParams = (path, attributionParams = {}) => {
  const params = buildAttributionSearchParams(attributionParams);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export const extractQLinkIdFromPath = (pathname = '') => {
  const match = String(pathname || '').match(QLINK_VIEW_PATH_PATTERN);
  if (!match?.[1]) {
    return undefined;
  }

  try {
    return normalizeValue(decodeURIComponent(match[1]));
  } catch (_) {
    return normalizeValue(match[1]);
  }
};

export const extractAttributionParams = (search = '') => {
  const params = new URLSearchParams(search || '');

  return ATTRIBUTION_PARAM_KEYS.reduce((acc, key) => {
    const value = normalizeValue(params.get(key));
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const getLocationOrigin = (location) => {
  if (location?.origin) {
    return location.origin;
  }

  if (location?.protocol && location?.host) {
    return `${location.protocol}//${location.host}`;
  }

  return undefined;
};

const sanitizeReferrer = (referrer, location) => {
  const normalized = normalizeValue(referrer);
  if (!normalized) {
    return undefined;
  }

  try {
    const currentOrigin = getLocationOrigin(location);
    const parsed = new URL(normalized, currentOrigin);
    if (currentOrigin && parsed.origin === currentOrigin) {
      return undefined;
    }

    return normalizeValue(`${parsed.origin}${parsed.pathname}`);
  } catch (_) {
    return undefined;
  }
};

const hasAttributionSignal = ({ params, referrer }) => (
  Object.keys(params || {}).length > 0 || !!normalizeValue(referrer)
);

const getFirstCapturedAt = (storedAttribution = {}) => (
  storedAttribution?.firstTouch?.capturedAt || storedAttribution?.latestTouch?.capturedAt
);

const isAttributionExpired = (storedAttribution, nowIso) => {
  const capturedAt = getFirstCapturedAt(storedAttribution);
  if (!capturedAt) {
    return false;
  }

  const capturedTime = Date.parse(capturedAt);
  const nowTime = Date.parse(nowIso);
  if (Number.isNaN(capturedTime) || Number.isNaN(nowTime)) {
    return false;
  }

  return nowTime - capturedTime > MAX_ATTRIBUTION_AGE_MS;
};

const safeClearAttribution = (storage) => {
  try {
    storage?.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch (_) {
    // Attribution cleanup should never break the app.
  }
};

const safeReadAttribution = (storage, nowIso = new Date().toISOString()) => {
  try {
    const rawValue = storage?.getItem(ATTRIBUTION_STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : {};
    if (isAttributionExpired(parsed, nowIso)) {
      safeClearAttribution(storage);
      return {};
    }

    return parsed;
  } catch (_) {
    return {};
  }
};

const safeWriteAttribution = (storage, value) => {
  try {
    storage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch (_) {
    // Attribution is useful, but it should never break the app.
  }
};

const createTouch = ({ location, referrer, now }) => {
  const params = extractAttributionParams(location?.search);
  const path = location?.pathname || '/';
  const qLinkId = extractQLinkIdFromPath(path);

  if (qLinkId && !params.qlink_id) {
    params.qlink_id = qLinkId;
  }

  return {
    capturedAt: now(),
    landingPath: buildPathFromParams(path, params),
    referrer: sanitizeReferrer(referrer, location),
    params,
  };
};

export const persistAttribution = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
  referrer = typeof document !== 'undefined' ? document.referrer : '',
  storage = typeof window !== 'undefined' ? window.localStorage : undefined,
  now = () => new Date().toISOString(),
} = {}) => {
  const nowIso = now();
  const touch = createTouch({ location, referrer, now: () => nowIso });
  const existing = safeReadAttribution(storage, nowIso);

  if (!hasAttributionSignal(touch)) {
    return existing;
  }

  const nextValue = {
    firstTouch: existing.firstTouch || touch,
    latestTouch: touch,
  };

  safeWriteAttribution(storage, nextValue);
  return nextValue;
};

export const readStoredAttribution = (
  storage = typeof window !== 'undefined' ? window.localStorage : undefined,
  now = () => new Date().toISOString()
) => safeReadAttribution(storage, now());

export const getAttributionParams = (storedAttribution = readStoredAttribution()) => ({
  ...(storedAttribution?.firstTouch?.params || {}),
  ...(storedAttribution?.latestTouch?.params || {}),
});

const toCamelCase = (key) => key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

export const getBackendAttribution = (storedAttribution = readStoredAttribution()) => {
  const firstTouch = storedAttribution?.firstTouch;
  const touch = firstTouch || storedAttribution?.latestTouch;
  const params = getAttributionParams(storedAttribution);

  if (!touch && Object.keys(params).length === 0) {
    return undefined;
  }

  const attribution = Object.keys(params).reduce((acc, key) => {
    acc[toCamelCase(key)] = params[key];
    return acc;
  }, {});

  if (touch?.landingPath) {
    attribution.landingPath = touch.landingPath;
  }

  if (touch?.referrer) {
    attribution.referrer = touch.referrer;
  }

  if (touch?.capturedAt) {
    attribution.capturedAt = touch.capturedAt;
  }

  if (firstTouch?.capturedAt) {
    attribution.firstCapturedAt = firstTouch.capturedAt;
  }

  return Object.keys(attribution).length ? attribution : undefined;
};

export const buildPathWithAttribution = (path, {
  attributionParams = getAttributionParams(),
  extraParams = {},
} = {}) => {
  const params = buildAttributionSearchParams(attributionParams);

  Object.entries(extraParams).forEach(([key, value]) => {
    const normalized = normalizeValue(value);
    if (normalized) {
      params.set(key, normalized);
    }
  });

  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

const getQLinkStateTouch = (storedAttribution = {}) => (
  storedAttribution?.firstTouch || storedAttribution?.latestTouch || {}
);

const stripQueryAndFragment = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return undefined;
  }

  const queryIndex = normalized.indexOf('?');
  const fragmentIndex = normalized.indexOf('#');
  let endIndex = normalized.length;

  if (queryIndex >= 0) {
    endIndex = Math.min(endIndex, queryIndex);
  }
  if (fragmentIndex >= 0) {
    endIndex = Math.min(endIndex, fragmentIndex);
  }

  return normalizeValue(normalized.substring(0, endIndex));
};

const encodeBase64Url = (value) => {
  let base64Value;

  if (typeof btoa === 'function') {
    base64Value = btoa(unescape(encodeURIComponent(value)));
  } else if (typeof Buffer !== 'undefined') {
    base64Value = Buffer.from(value, 'utf8').toString('base64');
  }

  if (!base64Value) {
    return undefined;
  }

  return base64Value
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

export const buildQLinkOAuthState = ({
  qlinkId,
  platform = 'unknown',
  storedAttribution = readStoredAttribution(),
  attributionParams,
} = {}) => {
  const normalizedQLinkId = normalizeValue(qlinkId);
  if (!normalizedQLinkId) {
    return undefined;
  }

  const normalizedPlatform = normalizeValue(platform) || 'unknown';
  const params = attributionParams || getAttributionParams(storedAttribution);
  const payload = {};

  QLINK_OAUTH_STATE_PARAM_KEYS.forEach((key) => {
    const value = normalizeValue(params?.[key]);
    if (value) {
      payload[key] = value;
    }
  });

  const touch = getQLinkStateTouch(storedAttribution);
  const landingPath = stripQueryAndFragment(touch?.landingPath);
  const referrer = stripQueryAndFragment(touch?.referrer);

  if (landingPath) {
    payload.landing_path = landingPath;
  }
  if (referrer) {
    payload.referrer = referrer;
  }

  const baseState = `qlink:${encodeURIComponent(normalizedQLinkId)}:${encodeURIComponent(normalizedPlatform)}`;
  const encodedPayload = Object.keys(payload).length
    ? encodeBase64Url(JSON.stringify(payload))
    : undefined;

  return encodedPayload ? `${baseState}:${encodedPayload}` : baseState;
};

const normalizeSafePathParam = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized || !/^\/($|[^/\\])/.test(normalized)) {
    return undefined;
  }

  return normalized;
};

// Only path-like extras should use this helper; non-path auth tokens must stay
// on their dedicated routes and out of sanitized attribution URLs.
const buildSafeExtraParams = (sourceParams, safeExtraParamKeys = []) => (
  safeExtraParamKeys.reduce((acc, key) => {
    const value = normalizeSafePathParam(sourceParams.get(key));
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {})
);

export const buildPathFromSafeQuery = (pathname, search = '', {
  hash = '',
  safeExtraParamKeys = [],
} = {}) => {
  const sourceParams = new URLSearchParams(search || '');
  const params = buildAttributionSearchParams(extractAttributionParams(search));
  const safeExtraParams = buildSafeExtraParams(sourceParams, safeExtraParamKeys);

  Object.entries(safeExtraParams).forEach(([key, value]) => {
    params.set(key, value);
  });

  const query = params.toString();
  return `${pathname || '/'}${query ? `?${query}` : ''}${hash || ''}`;
};

export const getSafeNextPath = (search = '', {
  allowedPathnames = [],
} = {}) => {
  const sourceParams = new URLSearchParams(search || '');
  const next = normalizeSafePathParam(sourceParams.get('next'));

  if (!next) {
    return undefined;
  }

  try {
    const parsed = new URL(next, 'https://qlearn.local');
    if (!allowedPathnames.length || !allowedPathnames.includes(parsed.pathname)) {
      return undefined;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_) {
    return undefined;
  }
};

export const sanitizeCurrentUrlQuery = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
  history = typeof window !== 'undefined' ? window.history : undefined,
  allowedPathnames = [],
  safeExtraParamKeys = [],
} = {}) => {
  if (!location?.pathname || !location?.search || typeof history?.replaceState !== 'function') {
    return false;
  }

  if (allowedPathnames.length === 0 || !allowedPathnames.includes(location.pathname)) {
    return false;
  }

  const currentPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const sanitizedPath = buildPathFromSafeQuery(location.pathname, location.search, {
    hash: location.hash,
    safeExtraParamKeys,
  });

  if (currentPath === sanitizedPath) {
    return false;
  }

  history.replaceState(history.state || null, '', sanitizedPath);
  return true;
};

export const clearConsumedReferralParam = ({
  location = typeof window !== 'undefined' ? window.location : undefined,
  history = typeof window !== 'undefined' ? window.history : undefined,
} = {}) => {
  if (!location?.pathname || !location?.search || typeof history?.replaceState !== 'function') {
    return false;
  }

  const params = new URLSearchParams(location.search);
  if (!params.has('ref')) {
    return false;
  }

  params.delete('ref');
  const query = params.toString();
  const sanitizedPath = `${location.pathname}${query ? `?${query}` : ''}${location.hash || ''}`;
  history.replaceState(history.state || null, '', sanitizedPath);
  return true;
};

// Convert a stored ISO-8601 referral-capture instant to the epoch-millis Long
// the backend SignupRequest.referralCapturedAtEpochMs expects (its 30d
// freshness guard, QLearn-service #466, is dormant until a client forwards
// this). Returns null for a missing or unparseable value so the caller omits
// the field — the backend treats an absent timestamp as "no client freshness
// signal", never an error. `parse` is injectable for tests. (The storage key
// itself lives in utils/referral.js as REFERRAL_CODE_CAPTURED_AT_KEY — single
// source of truth with the capture path.)
export const referralCapturedAtEpochMs = (isoString, parse = Date.parse) => {
  if (typeof isoString !== 'string' || isoString === '') {
    return null;
  }
  const ms = parse(isoString);
  return Number.isFinite(ms) ? ms : null;
};
